import { Link, useLocation, useNavigate } from "react-router";
import { Boxes, CirclePlus, FolderPlus, PackagePlus, Truck } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BrandMark } from "@/components/brand-mark";
import { Progress } from "@/components/ui/progress";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { usePreferences } from "@/components/providers/preferences-provider";
import { Can, useAbility } from "@/lib/auth/ability";
import { isNavActive, visibleNavGroups } from "@/lib/navigation";
import { useDashboard, useNavCounts } from "@/lib/queries";

import { NavUser, type ShellUser } from "./nav-user";

function QuickCreate() {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const ability = useAbility();
  const canCreate = ability.can("create", "Product") || ability.can("create", "Category");
  const go = (href: string) => {
    if (isMobile) setOpenMobile(false);
    navigate(href);
  };
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                tooltip="Quick create"
                className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground data-popup-open:bg-primary/90 data-popup-open:text-primary-foreground shadow-xs duration-200 ease-linear"
              />
            }
          >
            <CirclePlus />
            <span>Quick create</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "bottom" : "right"}
            align="start"
            sideOffset={8}
            className="w-56"
          >
            {canCreate ? (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Create</DropdownMenuLabel>
                  <Can I="create" a="Product">
                    <DropdownMenuItem onClick={() => go("/products/new")}>
                      <PackagePlus /> New product
                    </DropdownMenuItem>
                  </Can>
                  <Can I="create" a="Category">
                    <DropdownMenuItem onClick={() => go("/categories?new=1")}>
                      <FolderPlus /> New category
                    </DropdownMenuItem>
                  </Can>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuGroup>
              <DropdownMenuLabel>Operations</DropdownMenuLabel>
              <Can I="update" a="Inventory">
                <DropdownMenuItem onClick={() => go("/inventory?status=low_stock")}>
                  <Boxes /> Restock low items
                </DropdownMenuItem>
              </Can>
              <Can I="update" a="Delivery">
                <DropdownMenuItem onClick={() => go("/deliveries?agentId=none")}>
                  <Truck /> Assign tomorrow’s drops
                </DropdownMenuItem>
              </Can>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function RunCard() {
  const { data } = useDashboard();
  if (!data) return null;
  const { total, unassigned } = data.deliveriesTomorrow;
  const assigned = total ? Math.round(((total - unassigned) / total) * 100) : 0;
  return (
    <div className="bg-card text-card-foreground rounded-lg border p-3 shadow-xs group-data-[collapsible=icon]:hidden">
      <p className="text-xs font-medium">Tomorrow’s run</p>
      <p className="text-muted-foreground mt-0.5 text-xs">
        {total} drops · {unassigned ? `${unassigned} need a partner` : "all assigned"}
      </p>
      <Progress value={assigned} className="mt-2.5" aria-label="Drops assigned to a partner" />
    </div>
  );
}

export function AppSidebar({ user }: { user: ShellUser }) {
  const { pathname } = useLocation();
  const { preferences } = usePreferences();
  const { data: counts } = useNavCounts();
  const { isMobile, setOpenMobile } = useSidebar();
  const ability = useAbility();

  return (
    <Sidebar variant={preferences.sidebarVariant} collapsible={preferences.sidebarCollapsible}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link to="/" />} tooltip="Milqit Admin">
              <BrandMark />
              <span className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold">Milqit</span>
                <span className="text-muted-foreground truncate text-xs">Admin</span>
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <QuickCreate />
          </SidebarGroupContent>
        </SidebarGroup>
        {visibleNavGroups(ability).map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const count = item.badge ? counts?.[item.badge] : undefined;
                  return (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        render={
                          <Link
                            to={item.url}
                            onClick={() => {
                              if (isMobile) setOpenMobile(false);
                            }}
                          />
                        }
                        isActive={isNavActive(pathname, item.url)}
                        tooltip={count ? `${item.title} · ${count}` : item.title}
                      >
                        <Icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                      {count ? (
                        <SidebarMenuBadge className="bg-primary/10 text-primary rounded-md px-1.5 tabular-nums">
                          {count}
                        </SidebarMenuBadge>
                      ) : null}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <RunCard />
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
